import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import styles from './LoginPage.module.scss';

interface LoginForm {
  email: string;
  password: string;
}

export const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      console.log('[Login] Attempting login for:', data.email);
      console.log(
        '[Login] API URL:',
        import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      );

      // Clear password from memory after use for security
      const credentials = { ...data };

      await login(credentials.email, credentials.password);

      // Clear credentials from memory
      credentials.password = '';
      delete (data as any).password;

      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error: any) {
      console.error('[Login] Login failed:', error);

      // Handle specific error types
      const errorMessage = error.message || '';

      if (errorMessage.includes('Too many login attempts from this IP')) {
        toast.error(
          'Too many login attempts from this location. Please wait 5 minutes before trying again.'
        );
      } else if (
        errorMessage.includes('Account temporarily locked') ||
        errorMessage.includes('Account is locked')
      ) {
        toast.error(
          'Account temporarily locked due to failed login attempts. Please try again in 30 minutes.'
        );
      } else if (errorMessage.includes('Invalid credentials')) {
        toast.error(
          'Invalid email or password. Please check your credentials.'
        );
      } else {
        toast.error(errorMessage || 'Login failed. Please try again.');
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.formCard}>
          {/* Logo */}
          <div className={styles.logo}>
            <div className={styles.logoIcon}>
              <div className={styles.iconLayers}>
                <div className={styles.layerBack}></div>
                <div className={styles.layerMiddle}></div>
                <div className={styles.layerFront}></div>
              </div>
              <div className={styles.iconSpark}>✦</div>
            </div>
          </div>

          {/* Title */}
          <h1 className={styles.title}>Welcome back</h1>
          <p className={styles.subtitle}>
            Let's continue your learning journey.
          </p>

          {/* Google Button */}
          <button type="button" className={styles.googleButton}>
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className={styles.divider}>
            <span>or continue with</span>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <input
              type="email"
              placeholder="Enter your email"
              className={styles.input}
              autoFocus
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Please enter a valid email',
                },
              })}
            />
            {errors.email && (
              <span className={styles.error}>{errors.email.message}</span>
            )}

            <div className={styles.passwordContainer}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                className={styles.input}
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 6,
                    message: 'Password must be at least 6 characters',
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.passwordToggle}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <span className={styles.error}>{errors.password.message}</span>
            )}

            <Link to="/forgot-password" className={styles.forgotLink}>
              Forgot password?
            </Link>

            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? (
                <>
                  <div className={styles.spinner} />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Sign up link */}
          <p className={styles.signupText}>
            Don't have an account?{' '}
            <Link to="/register" className={styles.signupLink}>
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
